import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import SearchesHeader from '../components/SearchesHeader'
import FitScoreDisplay from '../components/FitScoreDisplay'
import AlternativesDropdown from '../components/AlternativesDropdown'
import DeeperAnalysisDropdown from '../components/DeeperAnalysisDropdown'
import ConfirmationModal from '../components/ConfirmationModal'

export default function Dashboard() {
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [jobInput, setJobInput] = useState('')
  const [analyzing, setAnalyzing] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')
  const [history, setHistory] = useState([])
  const [loadingStage, setLoadingStage] = useState('')
  const [loadingProgress, setLoadingProgress] = useState(0)
  const [userProfile, setUserProfile] = useState(null)
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [resultToSave, setResultToSave] = useState(null)
  const [deeperAnalysis, setDeeperAnalysis] = useState(null)
  const [deeperAnalyzing, setDeeperAnalyzing] = useState(false)
  const [deeperAnalysisProgress, setDeeperAnalysisProgress] = useState(0)
  const [deeperAnalysisError, setDeeperAnalysisError] = useState(null)
  const [deeperAnalysisRetries, setDeeperAnalysisRetries] = useState(0)
  const [showUncertaintyDetails, setShowUncertaintyDetails] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [currentAnalysisId, setCurrentAnalysisId] = useState(null)
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [searchingJobPostings, setSearchingJobPostings] = useState(false)

  useEffect(() => {
    console.log('[DASHBOARD] Checking authentication...')
    checkAuth()
  }, [])

  async function checkAuth() {
    try {
      console.log('[DASHBOARD] Starting auth check...')

      const { data: { session }, error } = await supabase.auth.getSession()

      if (error) {
        console.error('[DASHBOARD] Session error:', error)
        throw error
      }

      console.log('[DASHBOARD] Auth check complete:', { hasSession: !!session })

      if (!session) {
        console.log('[DASHBOARD] No session, redirecting to login...')
        navigate('/login', { replace: true })
        return
      }

      console.log('[DASHBOARD] User authenticated, loading profile...')
      setUser(session.user)
      await loadUserProfile(session.user.id)
      await loadHistory(session.user.id)
      setLoading(false)
    } catch (error) {
      console.error('[DASHBOARD] Auth check error:', error)
      navigate('/login', { replace: true })
    }
  }

  async function loadUserProfile(userId) {
    try {
      console.log('[DASHBOARD] Loading profile for user:', userId)
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) {
        console.error('[DASHBOARD] Error loading profile:', error)
        setError(`Failed to load profile: ${error.message}. Please refresh the page.`)
        return
      }

      if (data) {
        console.log('[DASHBOARD] Profile loaded successfully:', { role: data.role, id: data.id })
        await checkAndResetMonthlyQueries(data)
        setUserProfile(data)
      } else {
        console.log('[DASHBOARD] No profile found, creating one...')
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            role: 'user',
            queries_used: 0,
            queries_limit: 5,
            is_premium: false,
            monthly_reset_date: new Date().toISOString()
          })
          .select()
          .maybeSingle()

        if (insertError) {
          console.error('[DASHBOARD] Error creating profile:', insertError)
          setError(`Failed to create profile: ${insertError.message}. Please contact support.`)
        } else if (newProfile) {
          console.log('[DASHBOARD] Profile created successfully')
          setUserProfile(newProfile)
        } else {
          console.error('[DASHBOARD] Profile creation returned no data')
          setError('Failed to create profile. Please contact support.')
        }
      }
    } catch (err) {
      console.error('[DASHBOARD] Unexpected error loading profile:', err)
      setError('An unexpected error occurred. Please refresh the page.')
    }
  }

  async function checkAndResetMonthlyQueries(profile) {
    if (!profile.is_premium || !profile.monthly_reset_date) return

    const resetDate = new Date(profile.monthly_reset_date)
    const currentDate = new Date()

    const resetMonth = resetDate.getMonth()
    const resetYear = resetDate.getFullYear()
    const currentMonth = currentDate.getMonth()
    const currentYear = currentDate.getFullYear()

    const isNewMonth = (currentYear > resetYear) || (currentYear === resetYear && currentMonth > resetMonth)

    if (isNewMonth) {
      console.log('[DASHBOARD] New month detected, resetting query count')
      const { error } = await supabase
        .from('profiles')
        .update({
          queries_used: 0,
          monthly_reset_date: currentDate.toISOString()
        })
        .eq('id', profile.id)

      if (error) {
        console.error('Error resetting monthly queries:', error)
      } else {
        console.log('[DASHBOARD] Query count reset successfully')
        await loadUserProfile(profile.id)
      }
    }
  }

  async function loadHistory(userId) {
    const { data } = await supabase
      .from('analyses')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(10)

    if (data) {
      setHistory(data)
    }
  }

  async function checkQueryLimit() {
    console.log('[DASHBOARD] Checking query limit...', { hasProfile: !!userProfile, profile: userProfile })

    if (!userProfile) {
      console.error('[DASHBOARD] No user profile available')
      setError('Your profile is still loading. Please wait a moment and try again.')
      return false
    }

    if (userProfile.role === 'revoked') {
      setError('Your access has been revoked. Please contact support.')
      return false
    }

    if (userProfile.is_premium || userProfile.role === 'admin') {
      console.log('[DASHBOARD] User has unlimited queries')
      return true
    }

    const queriesUsed = userProfile.queries_used || 0
    const queriesLimit = userProfile.queries_limit || 5

    console.log('[DASHBOARD] Query usage:', { queriesUsed, queriesLimit })

    if (queriesUsed >= queriesLimit) {
      setError(`You've reached your monthly limit of ${queriesLimit} free analyses. Upgrade for unlimited access!`)
      return false
    }

    return true
  }

  async function handleAnalyze(e) {
    e.preventDefault()
    console.log('=== BUTTON CLICKED ===')
    setError('')
    console.log('Starting analysis...', { jobInput, userProfile })

    if (!jobInput || jobInput.trim().length < 50) {
      setError('Job description must be at least 50 characters.')
      console.log('Job description too short')
      return
    }

    const canProceed = await checkQueryLimit()
    console.log('Query limit check:', canProceed)
    if (!canProceed) return

    setAnalyzing(true)
    setResult(null)
    setLoadingProgress(0)
    setLoadingStage('Parsing job posting...')

    try {
      setLoadingProgress(15)
      setLoadingStage('Searching for duplicate postings...')

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-job`
      console.log('Calling API:', apiUrl)

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ jobDescription: jobInput })
      })

      console.log('API response status:', response.status)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
        console.error('API error:', errorData)
        throw new Error(errorData.error || 'Analysis failed')
      }

      setLoadingProgress(60)
      setLoadingStage('Identifying companies...')
      const data = await response.json()
      setLoadingProgress(85)
      console.log('Analysis result:', data)

      if (data.error) {
        throw new Error(data.error)
      }

      // Validate response structure
      if (!data.companies || !Array.isArray(data.companies)) {
        console.error('[DASHBOARD] Invalid response structure:', data)
        throw new Error('Invalid analysis response - missing companies array')
      }

      if (data.companies.length === 0) {
        console.warn('[DASHBOARD] No companies returned in analysis')
        throw new Error('No matching companies found. Please try with more specific job details.')
      }

      // Ensure all required fields have defaults
      data.companies = data.companies.map((company, index) => ({
        ...company,
        confidence: company.confidence ?? 0,
        locationMatch: company.locationMatch ?? 1,
        techMatch: company.techMatch ?? 1,
        businessFit: company.businessFit ?? 1,
        hiringPattern: company.hiringPattern ?? 1,
        phraseMatches: company.phraseMatches ?? [],
        evidenceQuality: company.evidenceQuality ?? 'INFERRED',
        techInferred: company.techInferred ?? false,
        expansionSignals: company.expansionSignals ?? [],
        isEquallyLikely: company.isEquallyLikely ?? false
      }))

      data.uncertaintyScore = data.uncertaintyScore ?? 0
      data.requiresFurtherAnalysis = data.requiresFurtherAnalysis ?? false
      data.uncertaintyFlags = data.uncertaintyFlags ?? []

      setLoadingProgress(95)
      setLoadingStage('Calculating confidence scores...')
      setResult(data)
      setLoadingProgress(100)

      const topCompanyConfidence = data.companies && data.companies.length > 0
        ? data.companies[0].confidence
        : 0

      const equallyLikelyCount = data.companies?.filter(c => c.isEquallyLikely).length || 0
      const allPhraseMatches = data.companies?.flatMap(c => c.phraseMatches || []) || []
      const allExpansionSignals = data.companies?.flatMap(c => c.expansionSignals || []) || []
      const topCompany = data.companies && data.companies.length > 0 ? data.companies[0] : null
      const hasTechInferred = topCompany?.techInferred || false
      const topEvidenceQuality = topCompany?.evidenceQuality || 'INFERRED'
      const skipDeeperAnalysis = topCompanyConfidence >= 85 && topEvidenceQuality === 'CONFIRMED'

      const { data: insertedAnalysis, error: insertError } = await supabase.from('analyses').insert({
        user_id: user.id,
        job_text: jobInput.substring(0, 1000),
        job_url: null,
        result_json: data,
        confidence_score: Math.min(5, Math.ceil(topCompanyConfidence / 20)),
        uncertainty_score: data.uncertaintyScore || 0,
        requires_further_analysis: data.requiresFurtherAnalysis || false,
        uncertainty_flags: data.uncertaintyFlags || [],
        analysis_depth: 1,
        phrase_matches: allPhraseMatches,
        tech_inferred: hasTechInferred,
        multiple_top_candidates: equallyLikelyCount > 1,
        expansion_signals: allExpansionSignals,
        evidence_quality: topEvidenceQuality,
        equally_likely_count: equallyLikelyCount,
        skip_deeper_analysis: skipDeeperAnalysis,
        ats_platforms_searched: data.atsSearches ? ['workday', 'greenhouse', 'lever', 'icims', 'taleo'] : [],
        scoring_breakdown: {
          top_company: topCompany?.name || 'N/A',
          location_score: topCompany?.locationMatch || 0,
          tech_score: topCompany?.techMatch || 0,
          business_score: topCompany?.businessFit || 0,
          hiring_score: topCompany?.hiringPattern || 0,
          composite_fit: topCompanyConfidence
        }
      }).select().maybeSingle()

      if (insertedAnalysis) {
        setCurrentAnalysisId(insertedAnalysis.id)
      }

      await supabase
        .from('profiles')
        .update({ queries_used: userProfile.queries_used + 1 })
        .eq('id', user.id)

      await loadUserProfile(user.id)
      loadHistory(user.id)
    } catch (err) {
      setError(err.message || 'Analysis failed. Please try again or contact support.')
      console.error('Analysis error:', err)
    }

    setAnalyzing(false)
    setLoadingStage('')
    setLoadingProgress(0)
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/')
  }

  function shouldShowDeeperAnalysis() {
    if (!result || !result.companies || result.companies.length === 0) return false

    const topConfidence = result.companies[0]?.confidence || 0
    const topEvidenceQuality = result.companies[0]?.evidenceQuality || 'WEAK'

    if (topConfidence >= 85 && topEvidenceQuality === 'CONFIRMED') {
      return false
    }

    return true
  }

  async function handleSearchJobPostings() {
    if (!result || !result.companies || result.companies.length === 0) return

    setSearchingJobPostings(true)
    const topCompany = result.companies[0]
    const jobTitle = jobInput.split('\n')[0] || 'job'
    const location = jobInput.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*),?\s+([A-Z]{2})/)
    const locationStr = location ? `${location[1]}, ${location[2]}` : ''

    const searchQuery = `${topCompany.name} ${jobTitle} ${locationStr}`.trim()
    const encodedQuery = encodeURIComponent(searchQuery)

    const searches = [
      { name: 'LinkedIn', url: `https://www.linkedin.com/jobs/search/?keywords=${encodedQuery}` },
      { name: 'Indeed', url: `https://www.indeed.com/jobs?q=${encodedQuery}` },
      { name: 'Glassdoor', url: `https://www.glassdoor.com/Job/jobs.htm?sc.keyword=${encodedQuery}` },
      { name: 'Company Site', url: `https://www.google.com/search?q=site:${topCompany.name.toLowerCase().replace(/\s+/g, '')}.com+careers+${encodeURIComponent(jobTitle)}` }
    ]

    searches.forEach(search => {
      window.open(search.url, '_blank', 'noopener,noreferrer')
    })

    setSearchingJobPostings(false)
    setSuccessMessage('✓ Opened 4 job search tabs!')
    setTimeout(() => setSuccessMessage(''), 4000)
  }

  async function handleDeeperAnalysis(isRetry = false) {
    if (!result || !(userProfile?.is_premium || userProfile?.role === 'admin')) return

    if (!isRetry) {
      setDeeperAnalysisRetries(0)
      setDeeperAnalysisError(null)
    }

    setDeeperAnalyzing(true)
    setDeeperAnalysisProgress(0)
    setError('')

    const progressInterval = setInterval(() => {
      setDeeperAnalysisProgress(prev => Math.min(prev + 8, 90))
    }, 1000)

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-job`
      console.log('Calling deeper analysis API:', apiUrl, 'Retry:', isRetry, 'Attempts:', deeperAnalysisRetries)

      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 140000)

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          jobDescription: jobInput,
          deeperAnalysis: true,
          previousResult: result
        }),
        signal: controller.signal
      })

      clearTimeout(timeoutId)
      console.log('Deeper analysis response status:', response.status)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `Server returned ${response.status}` }))
        console.error('API error:', errorData)
        throw new Error(errorData.error || 'Deeper analysis failed')
      }

      const data = await response.json()
      console.log('Deeper analysis result:', data)

      if (data.error) {
        throw new Error(data.error)
      }

      // Robust null checking for deeper analysis response
      if (!data.deeper_verification) {
        console.warn('[DASHBOARD] No deeper_verification in response, creating default')
        data.deeper_verification = {
          certainty_metric: 0,
          verification_summary: 'Analysis completed but verification data is incomplete',
          confidence_breakdown: {}
        }
      }

      if (data.deeper_verification.certainty_metric === undefined || data.deeper_verification.certainty_metric === null) {
        console.warn('[DASHBOARD] Missing certainty_metric, setting to 0')
        data.deeper_verification.certainty_metric = 0
      }

      // Ensure certainty_metric is a valid number between 0-100
      if (typeof data.deeper_verification.certainty_metric !== 'number' || isNaN(data.deeper_verification.certainty_metric)) {
        console.warn('[DASHBOARD] Invalid certainty_metric, setting to 0')
        data.deeper_verification.certainty_metric = 0
      } else {
        data.deeper_verification.certainty_metric = Math.max(0, Math.min(100, data.deeper_verification.certainty_metric))
      }

      clearInterval(progressInterval)
      setDeeperAnalysisProgress(100)
      setDeeperAnalysis(data.deeper_verification)
      setDeeperAnalysisError(null)
      setDeeperAnalysisRetries(0)

      setSuccessMessage('✓ Deeper analysis completed successfully!')
      setTimeout(() => setSuccessMessage(''), 4000)

      if (currentAnalysisId) {
        const { error: insertError } = await supabase.from('analysis_follow_ups').insert({
          analysis_id: currentAnalysisId,
          user_id: user.id,
          follow_up_depth: 2,
          uncertainty_reasons: result.uncertaintyFlags || [],
          result_json: data.deeper_verification,
          confidence_improvement: (data.deeper_verification?.certainty_metric || 0) - (result.companies[0]?.confidence || 0)
        })

        if (insertError) {
          console.warn('Failed to save follow-up:', insertError)
        }
      }
    } catch (err) {
      clearInterval(progressInterval)
      const errorMessage = err.name === 'AbortError'
        ? 'Analysis timed out after 140 seconds. The request may be too complex.'
        : err.message || 'Deeper analysis failed.'

      console.error('Deeper analysis error:', err)

      if (deeperAnalysisRetries < 2 && (err.name === 'AbortError' || errorMessage.includes('500') || errorMessage.includes('timed out'))) {
        setDeeperAnalysisError(errorMessage)
        setDeeperAnalysisRetries(prev => prev + 1)
      } else {
        setError(errorMessage + ' Please try again or contact support.')
        setDeeperAnalysisError(null)
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }

    setDeeperAnalyzing(false)
    setDeeperAnalysisProgress(0)
  }

  async function handleSaveSearch() {
    if (!saveName.trim() || !resultToSave) return

    const { error } = await supabase
      .from('saved_searches')
      .insert({
        user_id: user.id,
        search_name: saveName,
        job_text: jobInput,
        result_json: resultToSave
      })

    if (error) {
      console.error('Error saving search:', error)
      alert('Error saving search')
    } else {
      setShowSaveModal(false)
      setSaveName('')
      setResultToSave(null)
      alert('Search saved successfully!')
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#98FB98', fontSize: '14px' }}>Loading dashboard...</p>
      </div>
    )
  }

  if (!user || !userProfile) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#98FB98', fontSize: '14px' }}>Loading user data...</p>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <div className="nav">
        <div className="nav-content">
          <div
            className="nav-title"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <span>&gt; AgencySniper</span>
          </div>
          <div className="nav-actions">
            <button onClick={() => navigate('/saved')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Saved
            </button>
            <button onClick={() => navigate('/agencies')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Agencies
            </button>
            <button onClick={() => navigate('/recent-searches')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Recent
            </button>
            {userProfile?.role === 'admin' && (
              <button onClick={() => navigate('/admin')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
                Admin
              </button>
            )}
            <button onClick={handleSignOut} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <SearchesHeader userId={user?.id} userProfile={userProfile} />

      {successMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          background: 'linear-gradient(135deg, rgba(34, 139, 34, 0.95) 0%, rgba(0, 100, 0, 0.98) 100%)',
          backdropFilter: 'blur(16px)',
          color: '#98FB98',
          padding: '16px 24px',
          borderRadius: '12px',
          border: '2px solid #98FB98',
          boxShadow: '0 8px 24px rgba(34, 139, 34, 0.6), 0 0 40px rgba(152, 251, 152, 0.4)',
          fontSize: '15px',
          fontWeight: '600',
          textShadow: '0 0 10px rgba(152, 251, 152, 0.8)',
          animation: 'slideInRight 0.4s ease-out',
          maxWidth: '90vw'
        }} role="status" aria-live="polite">
          {successMessage}
        </div>
      )}

      <div className="container">
        {/* Quick Access Section */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '32px' }}>
          <div
            onClick={() => navigate('/recent-searches')}
            className="card"
            style={{
              cursor: 'pointer',
              padding: '24px',
              transition: 'all 0.3s ease',
              border: '2px solid rgba(152, 251, 152, 0.3)',
              background: 'rgba(34, 139, 34, 0.15)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)'
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(34, 139, 34, 0.4), 0 0 40px rgba(152, 251, 152, 0.5)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(34, 139, 34, 0.2), 0 0 20px rgba(152, 251, 152, 0.3)'
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#98FB98', marginBottom: '8px', textShadow: '0 0 10px rgba(152, 251, 152, 0.6)' }}>
              Recent Analyses
            </h3>
            <p style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.75)', lineHeight: '1.6' }}>
              View detailed results from past searches
            </p>
            <div style={{ marginTop: '12px', fontSize: '24px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 10px rgba(152, 251, 152, 0.7)' }}>
              {history.length}
            </div>
          </div>

          <div
            onClick={() => navigate('/saved')}
            className="card"
            style={{
              cursor: 'pointer',
              padding: '24px',
              transition: 'all 0.3s ease',
              border: '2px solid rgba(152, 251, 152, 0.3)',
              background: 'rgba(34, 139, 34, 0.15)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)'
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(34, 139, 34, 0.4), 0 0 40px rgba(152, 251, 152, 0.5)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(34, 139, 34, 0.2), 0 0 20px rgba(152, 251, 152, 0.3)'
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⭐</div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#98FB98', marginBottom: '8px', textShadow: '0 0 10px rgba(152, 251, 152, 0.6)' }}>
              Saved Searches
            </h3>
            <p style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.75)', lineHeight: '1.6' }}>
              Access your bookmarked analyses
            </p>
          </div>

          <div
            onClick={() => navigate('/agencies')}
            className="card"
            style={{
              cursor: 'pointer',
              padding: '24px',
              transition: 'all 0.3s ease',
              border: '2px solid rgba(152, 251, 152, 0.3)',
              background: 'rgba(34, 139, 34, 0.15)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)'
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(34, 139, 34, 0.4), 0 0 40px rgba(152, 251, 152, 0.5)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(34, 139, 34, 0.2), 0 0 20px rgba(152, 251, 152, 0.3)'
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🏢</div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#98FB98', marginBottom: '8px', textShadow: '0 0 10px rgba(152, 251, 152, 0.6)' }}>
              Agency Database
            </h3>
            <p style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.75)', lineHeight: '1.6' }}>
              Browse filtered recruitment agencies
            </p>
          </div>

          <div
            onClick={() => navigate('/history')}
            className="card"
            style={{
              cursor: 'pointer',
              padding: '24px',
              transition: 'all 0.3s ease',
              border: '2px solid rgba(152, 251, 152, 0.3)',
              background: 'rgba(34, 139, 34, 0.15)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)'
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(34, 139, 34, 0.4), 0 0 40px rgba(152, 251, 152, 0.5)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 6px 12px rgba(34, 139, 34, 0.2), 0 0 20px rgba(152, 251, 152, 0.3)'
            }}
          >
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#98FB98', marginBottom: '8px', textShadow: '0 0 10px rgba(152, 251, 152, 0.6)' }}>
              Search History
            </h3>
            <p style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.75)', lineHeight: '1.6' }}>
              Filter and export all analyses
            </p>
          </div>
        </div>

        {/* Analysis Section */}
        <div className="card">
          <h2 style={{ fontSize: '26px', marginBottom: '16px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
            &gt; Analyze Target Posting
          </h2>
          <p style={{ color: 'rgba(152, 251, 152, 0.9)', marginBottom: '28px', fontSize: '15px', lineHeight: '1.6', textShadow: '0 0 5px rgba(152, 251, 152, 0.4)' }}>
            Input job posting data to identify hidden client - Laser targeted results
          </p>

          <form onSubmit={handleAnalyze} aria-label="Job posting analysis form">
            <label htmlFor="job-description" className="sr-only">
              Job Description (minimum 50 characters)
            </label>
            <div style={{ position: 'relative' }}>
              <textarea
                id="job-description"
                value={jobInput}
                onChange={(e) => setJobInput(e.target.value)}
                placeholder="Example:

Controller
Construction
Oklahoma City, Oklahoma

We have partnered with a growing construction company to identify an experienced Controller...

Requirements:
- 5+ years construction accounting
- QuickBooks Online
- Job costing experience
- Bachelor's degree in Accounting

Compensation: $120-150K"
                required
                aria-required="true"
                aria-describedby={error ? 'analysis-error' : undefined}
                aria-invalid={!!error}
              />
              {jobInput && (
                <button
                  type="button"
                  onClick={() => setShowClearConfirm(true)}
                  className="btn-secondary"
                  style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    padding: '6px 12px',
                    fontSize: '11px',
                    minHeight: 'auto',
                    lineHeight: '1.4',
                    zIndex: 10
                  }}
                  aria-label="Clear job description text"
                >
                  Clear Text
                </button>
              )}
            </div>

            {error && <div className="error" id="analysis-error" role="alert" aria-live="polite">{error}</div>}

            <button
              type="submit"
              className="btn"
              disabled={analyzing}
              aria-label="Analyze job posting to identify hidden employer"
              aria-busy={analyzing}
              style={{ marginTop: '16px', position: 'relative', overflow: 'hidden' }}
            >
              {analyzing && loadingProgress > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '4px',
                    width: `${loadingProgress}%`,
                    background: 'rgba(152, 251, 152, 0.6)',
                    transition: 'width 0.5s ease',
                    boxShadow: '0 0 10px rgba(152, 251, 152, 0.8)'
                  }}
                  aria-hidden="true"
                />
              )}
              {analyzing ? (
                <span>
                  <span className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px', display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} aria-hidden="true"></span>
                  {loadingStage || 'Analyzing...'} ({loadingProgress}%)
                  <span className="sr-only">Analysis in progress: {loadingStage || 'Analyzing job posting'}, {loadingProgress} percent complete</span>
                </span>
              ) : 'Analyze Job Posting'}
            </button>
          </form>
        </div>

        {result && result.companies && result.companies.length > 0 && (
          <div className="card">
            {result.detectedAgencies && result.detectedAgencies.length > 0 && (
              <div style={{
                marginBottom: '24px',
                padding: '16px 20px',
                background: 'linear-gradient(135deg, rgba(152, 251, 152, 0.2) 0%, rgba(34, 139, 34, 0.3) 100%)',
                border: '2px solid #98FB98',
                borderRadius: '8px',
                boxShadow: '0 6px 12px rgba(34, 139, 34, 0.3), 0 0 25px rgba(152, 251, 152, 0.4)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>🎯</span>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#98FB98', marginBottom: '4px', textShadow: '0 0 10px rgba(152, 251, 152, 0.7)' }}>
                      {result.detectedAgencies.length} {result.detectedAgencies.length === 1 ? 'Agency' : 'Agencies'} Detected from Alternatives
                    </div>
                    <div style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.9)' }}>
                      <strong style={{ color: '#98FB98' }}>{result.detectedAgencies.join(', ')}</strong> {result.detectedAgencies.length === 1 ? 'was' : 'were'} auto-detected and added to the agency database for future filtering
                    </div>
                  </div>
                </div>
              </div>
            )}
            {result.detectedAgency && result.agencyAdded && (
              <div style={{
                marginBottom: '24px',
                padding: '16px 20px',
                background: 'linear-gradient(135deg, rgba(152, 251, 152, 0.25) 0%, rgba(34, 139, 34, 0.4) 100%)',
                border: '2px solid #98FB98',
                borderRadius: '8px',
                boxShadow: '0 6px 12px rgba(34, 139, 34, 0.3), 0 0 25px rgba(152, 251, 152, 0.4)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '24px' }}>🎯</span>
                  <div>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: '#98FB98', marginBottom: '4px', textShadow: '0 0 10px rgba(152, 251, 152, 0.7)' }}>
                      Agency Detected & Added
                    </div>
                    <div style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.9)' }}>
                      <strong style={{ color: '#98FB98' }}>{result.detectedAgency}</strong> was auto-detected and added to the agency database for future filtering
                    </div>
                  </div>
                </div>
              </div>
            )}
            {(result.detectedIndustry || result.platformsDetected) && (
              <div style={{
                marginBottom: '24px',
                padding: '16px 20px',
                background: 'rgba(34, 139, 34, 0.15)',
                border: '2px solid rgba(152, 251, 152, 0.4)',
                borderRadius: '8px',
                boxShadow: '0 4px 10px rgba(34, 139, 34, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '16px', flexWrap: 'wrap' }}>
                  {result.detectedIndustry && (
                    <div style={{ flex: '1 1 200px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '20px' }}>🏢</span>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 8px rgba(152, 251, 152, 0.6)' }}>
                          Industry Detected
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          padding: '6px 14px',
                          background: 'rgba(152, 251, 152, 0.2)',
                          border: '1px solid rgba(152, 251, 152, 0.5)',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '700',
                          color: '#98FB98',
                          textShadow: '0 0 8px rgba(152, 251, 152, 0.5)'
                        }}>
                          {result.detectedIndustry}
                        </span>
                        {result.industryConfidence && (
                          <span style={{ fontSize: '13px', color: 'rgba(152, 251, 152, 0.8)' }}>
                            {result.industryConfidence}% confidence
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  {result.platformsDetected && result.platformsDetected.length > 0 && (
                    <div style={{ flex: '1 1 200px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '20px' }}>🔍</span>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 8px rgba(152, 251, 152, 0.6)' }}>
                          ATS Platforms Detected
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {result.platformsDetected.map((platform, idx) => (
                          <span
                            key={idx}
                            style={{
                              padding: '4px 10px',
                              background: 'rgba(255, 200, 100, 0.2)',
                              border: '1px solid rgba(255, 200, 100, 0.5)',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              color: 'rgba(255, 200, 100, 0.95)'
                            }}
                          >
                            {platform}
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: '11px', color: 'rgba(152, 251, 152, 0.7)', marginTop: '6px', fontStyle: 'italic' }}>
                        Agency-posted job indicator
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <AlternativesDropdown
              alternatives={result.allAlternatives}
              isPremiumOrAdmin={userProfile?.is_premium || userProfile?.role === 'admin'}
            />

            {result.requiresFurtherAnalysis && result.uncertaintyScore > 0 && (userProfile?.is_premium || userProfile?.role === 'admin') && (
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'linear-gradient(135deg, rgba(255, 200, 100, 0.15) 0%, rgba(255, 150, 50, 0.2) 100%)',
                border: '2px solid rgba(255, 200, 100, 0.5)',
                borderRadius: '12px',
                boxShadow: '0 6px 12px rgba(255, 150, 50, 0.3), 0 0 25px rgba(255, 200, 100, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                  <div style={{ fontSize: '32px' }}>⚠️</div>
                  <div style={{ flex: 1 }}>
                    {deeperAnalysisError && deeperAnalysisRetries < 2 && (
                      <div style={{
                        marginBottom: '16px',
                        padding: '12px 16px',
                        background: 'rgba(255, 107, 107, 0.15)',
                        border: '2px solid rgba(255, 107, 107, 0.5)',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap'
                      }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#FF6B6B', marginBottom: '4px' }}>
                            Analysis Failed (Attempt {deeperAnalysisRetries}/2)
                          </div>
                          <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.85)' }}>
                            {deeperAnalysisError}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeeperAnalysis(true)}
                          disabled={deeperAnalyzing}
                          className="btn-secondary"
                          style={{ padding: '8px 16px', fontSize: '13px', minHeight: 'auto', whiteSpace: 'nowrap' }}
                        >
                          🔄 Retry ({2 - deeperAnalysisRetries} left)
                        </button>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#FFA500', marginBottom: '6px', textShadow: '0 0 10px rgba(255, 165, 0, 0.6)' }}>
                          Uncertainty Detected
                        </div>
                        <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '12px' }}>
                          This analysis has an uncertainty score of <strong>{result.uncertaintyScore}%</strong>. Further investigation recommended to improve confidence.
                        </div>
                      </div>
                      <div style={{
                        padding: '6px 12px',
                        background: 'rgba(255, 165, 0, 0.3)',
                        border: '1px solid rgba(255, 165, 0, 0.6)',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '700',
                        color: '#FFA500',
                        whiteSpace: 'nowrap'
                      }}>
                        {result.uncertaintyScore}% Uncertain
                      </div>
                    </div>

                    {result.specificRequirements && result.specificRequirements.length > 0 && (
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.95)', marginBottom: '8px' }}>
                          Recommended Focus Areas:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: 'rgba(255, 255, 255, 0.85)', fontSize: '13px', lineHeight: '1.7' }}>
                          {result.specificRequirements.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {result.uncertaintyFlags && result.uncertaintyFlags.length > 0 && (
                      <details style={{ marginBottom: '12px' }}>
                        <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: 'rgba(255, 255, 255, 0.9)', marginBottom: '8px' }}>
                          View Technical Flags ({result.uncertaintyFlags.length})
                        </summary>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                          {result.uncertaintyFlags.map((flag, idx) => (
                            <span key={idx} style={{
                              padding: '4px 10px',
                              background: 'rgba(255, 165, 0, 0.2)',
                              border: '1px solid rgba(255, 165, 0, 0.4)',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              color: 'rgba(255, 200, 100, 0.95)',
                              fontFamily: 'monospace'
                            }}>
                              {flag.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </details>
                    )}

                    <button
                      onClick={handleDeeperAnalysis}
                      disabled={deeperAnalyzing}
                      className="btn"
                      aria-label="Perform deeper analysis to resolve uncertainties"
                      aria-busy={deeperAnalyzing}
                      style={{
                        marginTop: '8px',
                        padding: '10px 20px',
                        fontSize: '14px',
                        background: 'linear-gradient(135deg, #FFA500 0%, #FF8C00 100%)',
                        border: '2px solid rgba(255, 200, 100, 0.6)',
                        boxShadow: '0 4px 8px rgba(255, 165, 0, 0.4)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {deeperAnalyzing && (
                        <div style={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          height: '3px',
                          width: `${deeperAnalysisProgress}%`,
                          background: 'rgba(255, 255, 255, 0.5)',
                          transition: 'width 0.5s ease'
                        }} aria-hidden="true" />
                      )}
                      {deeperAnalyzing ? (
                        <span>
                          <span className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px', display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} aria-hidden="true"></span>
                          Analyzing Further... {deeperAnalysisProgress}%
                          <span className="sr-only">Analysis in progress, {deeperAnalysisProgress} percent complete</span>
                        </span>
                      ) : (
                        <>
                          🔬 Analyze Further - Resolve Uncertainties
                          <span style={{ fontSize: '10px', marginLeft: '8px', padding: '2px 6px', background: 'rgba(255, 255, 255, 0.3)', borderRadius: '4px' }} aria-label={`${userProfile?.role === 'admin' ? 'Admin' : 'Premium'} feature`}>
                            {userProfile?.role === 'admin' ? 'ADMIN' : 'PREMIUM'}
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!shouldShowDeeperAnalysis() && (userProfile?.is_premium || userProfile?.role === 'admin') && (
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: 'linear-gradient(135deg, rgba(152, 251, 152, 0.25) 0%, rgba(34, 139, 34, 0.4) 100%)',
                border: '2px solid rgba(152, 251, 152, 0.6)',
                borderRadius: '12px',
                boxShadow: '0 6px 12px rgba(34, 139, 34, 0.3), 0 0 25px rgba(152, 251, 152, 0.4)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '28px' }}>✓</span>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 12px rgba(152, 251, 152, 0.8)' }}>
                      High Confidence Result
                    </div>
                    <div style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.95)', marginTop: '4px' }}>
                      Top match has {result.companies[0]?.confidence}% confidence with {result.companies[0]?.evidenceQuality} evidence quality - no further analysis needed
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleSearchJobPostings}
                  disabled={searchingJobPostings}
                  className="btn"
                  style={{ padding: '10px 20px', fontSize: '14px', width: '100%' }}
                >
                  {searchingJobPostings ? 'Opening searches...' : '🔍 Search for Job Postings'}
                </button>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#98FB98', margin: 0, textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
                &gt; Analysis Results
              </h3>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {(userProfile?.is_premium || userProfile?.role === 'admin') && shouldShowDeeperAnalysis() && !result.requiresFurtherAnalysis && (
                  <button
                    onClick={handleDeeperAnalysis}
                    disabled={deeperAnalyzing}
                    className="btn"
                    aria-label="Perform optional deeper verification analysis"
                    aria-busy={deeperAnalyzing}
                    style={{ padding: '10px 20px', fontSize: '14px', position: 'relative', overflow: 'hidden' }}
                  >
                    {deeperAnalyzing && (
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: '3px',
                        width: `${deeperAnalysisProgress}%`,
                        background: 'rgba(255, 255, 255, 0.4)',
                        transition: 'width 0.5s ease'
                      }} aria-hidden="true" />
                    )}
                    {deeperAnalyzing ? (
                      <span>
                        <span className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px', display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} aria-hidden="true"></span>
                        Verifying... {deeperAnalysisProgress}%
                        <span className="sr-only">Verification in progress, {deeperAnalysisProgress} percent complete</span>
                      </span>
                    ) : (
                      <>
                        🔍 Deeper Analysis
                        <span style={{ fontSize: '10px', marginLeft: '6px', padding: '2px 6px', background: 'rgba(255, 255, 255, 0.2)', borderRadius: '4px' }} aria-label={`${userProfile?.role === 'admin' ? 'Admin' : 'Premium'} feature`}>{userProfile?.role === 'admin' ? 'ADMIN' : 'PREMIUM'}</span>
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={handleSearchJobPostings}
                  disabled={searchingJobPostings}
                  className="btn-secondary"
                  style={{ padding: '10px 20px', fontSize: '14px' }}
                >
                  {searchingJobPostings ? 'Opening...' : '🔍 Find Postings'}
                </button>
                <button
                  onClick={() => {
                    setResultToSave(result)
                    setShowSaveModal(true)
                  }}
                  className="btn"
                  style={{ padding: '10px 20px', fontSize: '14px' }}
                >
                  Save Search
                </button>
              </div>
            </div>

            {result.topClient && (
              <div style={{ marginBottom: '32px', padding: '28px', background: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', borderRadius: '20px', border: '2px solid rgba(255, 255, 255, 0.4)', boxShadow: '0 12px 40px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.4)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <h4 style={{ fontSize: '26px', fontWeight: '700', color: 'white', textShadow: '0 2px 6px rgba(0, 0, 0, 0.3)' }}>
                    {result.topClient.name}
                  </h4>
                  <span className="badge badge-high" style={{ fontSize: '15px' }}>
                    Top Match
                  </span>
                </div>
                <p style={{ color: 'rgba(255, 255, 255, 0.95)', marginBottom: '16px', lineHeight: '1.8', fontSize: '15px', textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)' }}>
                  {result.topClient.overview}
                </p>
                <p style={{ color: 'rgba(255, 255, 255, 0.9)', marginBottom: '20px', lineHeight: '1.7', fontSize: '14px', textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)' }}>
                  <strong style={{ color: 'white' }}>Why this match:</strong> {result.topClient.fit}
                </p>
                {result.topClient.url && (
                  <a
                    href={result.topClient.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'white', fontWeight: '600', textDecoration: 'none', background: 'rgba(255, 255, 255, 0.15)', padding: '10px 20px', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.3)', display: 'inline-block', transition: 'all 0.3s ease', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}
                  >
                    Visit Website →
                  </a>
                )}
              </div>
            )}

            {result.companies && result.companies.length >= 1 && (
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{ fontSize: '22px', marginBottom: '20px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
                  &gt; {result.companies.filter(c => c.isEquallyLikely).length > 1 ? 'Top Matches (Equally Likely)' : result.companies[0]?.confidence >= 75 ? 'Most Likely Matches' : 'Possible Matches'} ({result.companies.length} analyzed)
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {result.companies.map((company, index) => {
                    const compositeFit = (company.locationMatch + company.techMatch + company.businessFit + company.hiringPattern) / 4
                    return (
                      <details
                        key={index}
                        style={{
                          padding: '16px',
                          background: company.isEquallyLikely ? 'linear-gradient(135deg, rgba(34, 139, 34, 0.95) 0%, rgba(255, 215, 0, 0.15) 100%)' : '#228B22',
                          borderRadius: '12px',
                          border: company.isEquallyLikely ? '3px solid rgba(255, 215, 0, 0.8)' : '2px solid #98FB98',
                          boxShadow: company.isEquallyLikely ? '0 0 25px rgba(255, 215, 0, 0.4), 0 6px 18px rgba(34, 139, 34, 0.3)' : '0 0 15px rgba(152, 251, 152, 0.2)',
                          cursor: 'pointer',
                          position: 'relative'
                        }}
                      >
                        <summary style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', listStyle: 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '20px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 10px rgba(152, 251, 152, 0.5)' }}>
                              {index + 1}. {company.name}
                            </span>
                            <span className={`badge ${company.confidence > 70 ? 'badge-high' : company.confidence > 40 ? 'badge-medium' : 'badge-low'}`} style={{ fontSize: '13px' }}>
                              {company.confidence}%
                            </span>
                            {company.isEquallyLikely && (
                              <span style={{ padding: '6px 12px', background: 'rgba(255, 215, 0, 0.35)', border: '2px solid rgba(255, 215, 0, 0.8)', borderRadius: '8px', fontSize: '12px', fontWeight: '800', color: '#FFD700', textTransform: 'uppercase', textShadow: '0 0 10px rgba(255, 215, 0, 0.6)', boxShadow: '0 0 15px rgba(255, 215, 0, 0.3)' }}>
                                ⭐ Equally Likely Top Match
                              </span>
                            )}
                            {company.techInferred && (
                              <span title="Technology inferred from industry standards" style={{ padding: '4px 8px', background: 'rgba(255, 200, 100, 0.2)', border: '1px solid rgba(255, 200, 100, 0.4)', borderRadius: '4px', fontSize: '10px', fontWeight: '600', color: 'rgba(255, 200, 100, 0.9)' }}>
                                Tech Inferred
                              </span>
                            )}
                            {company.evidenceQuality && (
                              <span style={{ padding: '4px 8px', background: company.evidenceQuality === 'CONFIRMED' ? 'rgba(100, 255, 150, 0.2)' : company.evidenceQuality === 'INFERRED' ? 'rgba(255, 200, 100, 0.2)' : 'rgba(255, 107, 107, 0.2)', border: `1px solid ${company.evidenceQuality === 'CONFIRMED' ? 'rgba(100, 255, 150, 0.5)' : company.evidenceQuality === 'INFERRED' ? 'rgba(255, 200, 100, 0.5)' : 'rgba(255, 107, 107, 0.5)'}`, borderRadius: '4px', fontSize: '10px', fontWeight: '600', color: company.evidenceQuality === 'CONFIRMED' ? 'rgba(100, 255, 150, 0.95)' : company.evidenceQuality === 'INFERRED' ? 'rgba(255, 200, 100, 0.9)' : 'rgba(255, 107, 107, 0.9)' }}>
                                {company.evidenceQuality}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '14px', color: '#F5F5F5', marginLeft: '12px' }}>
                            Fit Score: {compositeFit.toFixed(1)}/5
                          </div>
                        </summary>
                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(152, 251, 152, 0.3)' }}>
                          <p style={{ fontSize: '14px', color: '#F5F5F5', lineHeight: '1.7', marginBottom: '16px' }}>
                            {company.reasoning}
                          </p>
                          {company.phraseMatches && company.phraseMatches.length > 0 && (
                            <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(152, 251, 152, 0.1)', borderRadius: '8px', border: '1px solid rgba(152, 251, 152, 0.3)' }}>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: '#98FB98', marginBottom: '8px' }}>🎯 Matched Phrases:</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {company.phraseMatches.map((phrase, pIdx) => (
                                  <span key={pIdx} style={{ padding: '4px 8px', background: 'rgba(152, 251, 152, 0.2)', border: '1px solid rgba(152, 251, 152, 0.4)', borderRadius: '4px', fontSize: '11px', color: 'rgba(152, 251, 152, 0.95)', fontFamily: 'monospace' }}>
                                    {phrase}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {company.expansionSignals && company.expansionSignals.length > 0 && (
                            <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(255, 215, 0, 0.1)', borderRadius: '8px', border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: '#FFD700', marginBottom: '8px' }}>📈 Growth Signals:</div>
                              <ul style={{ margin: 0, paddingLeft: '20px', color: 'rgba(255, 215, 0, 0.9)', fontSize: '12px', lineHeight: '1.6' }}>
                                {company.expansionSignals.map((signal, sIdx) => (
                                  <li key={sIdx}>{signal}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px' }}>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '20px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 8px rgba(152, 251, 152, 0.5)' }}>
                                {company.locationMatch}/5
                              </div>
                              <div style={{ fontSize: '11px', color: '#F5F5F5', marginTop: '4px', fontFamily: 'VT323, monospace' }}>Location</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '20px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 8px rgba(152, 251, 152, 0.5)' }}>
                                {company.techMatch}/5
                              </div>
                              <div style={{ fontSize: '11px', color: '#F5F5F5', marginTop: '4px', fontFamily: 'VT323, monospace' }}>Tech Stack</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '20px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 8px rgba(152, 251, 152, 0.5)' }}>
                                {company.businessFit}/5
                              </div>
                              <div style={{ fontSize: '11px', color: '#F5F5F5', marginTop: '4px', fontFamily: 'VT323, monospace' }}>Business Fit</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '20px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 8px rgba(152, 251, 152, 0.5)' }}>
                                {company.hiringPattern}/5
                              </div>
                              <div style={{ fontSize: '11px', color: '#F5F5F5', marginTop: '4px', fontFamily: 'VT323, monospace' }}>Hiring</div>
                            </div>
                          </div>
                        </div>
                      </details>
                    )
                  })}
                </div>
              </div>
            )}

            <h4 style={{ fontSize: '20px', marginBottom: '20px', fontWeight: '700', color: 'white', textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)' }}>
              📄 Detailed Analysis Data (Click to Expand)
            </h4>

            {result.companies.map((company, index) => (
              <details key={index} className="result-item" style={{ cursor: 'pointer' }}>
                <summary style={{ listStyle: 'none', cursor: 'pointer' }}>
                  <div className="result-header">
                    <div className="company-name">{company.name}</div>
                    <span className={`badge ${company.confidence > 70 ? 'badge-high' : company.confidence > 40 ? 'badge-medium' : 'badge-low'}`}>
                      {company.confidence}% match
                    </span>
                  </div>
                  <p className="reasoning">{company.reasoning}</p>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px', marginTop: '16px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: company.locationMatch >= 4 ? 'rgba(100, 255, 150, 0.95)' : 'rgba(255, 255, 255, 0.5)', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
                        {company.locationMatch}/5
                      </div>
                      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.75)', marginTop: '6px', fontWeight: '600', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>Location</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: company.techMatch >= 4 ? 'rgba(100, 255, 150, 0.95)' : 'rgba(255, 255, 255, 0.5)', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
                        {company.techMatch}/5
                      </div>
                      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.75)', marginTop: '6px', fontWeight: '600', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>Tech Stack</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: company.businessFit >= 4 ? 'rgba(100, 255, 150, 0.95)' : 'rgba(255, 255, 255, 0.5)', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
                        {company.businessFit}/5
                      </div>
                      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.75)', marginTop: '6px', fontWeight: '600', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>Business Fit</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: company.hiringPattern >= 4 ? 'rgba(100, 255, 150, 0.95)' : 'rgba(255, 255, 255, 0.5)', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
                        {company.hiringPattern}/5
                      </div>
                      <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.75)', marginTop: '6px', fontWeight: '600', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>Hiring</div>
                    </div>
                  </div>

                  <div style={{ marginTop: '16px', textAlign: 'center', color: 'rgba(152, 251, 152, 0.8)', fontSize: '13px', fontWeight: '600' }}>
                    Click to view detailed analysis data ▼
                  </div>
                </summary>

                <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '2px solid rgba(152, 251, 152, 0.3)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h5 style={{ fontSize: '16px', fontWeight: '700', color: '#98FB98', margin: 0, textShadow: '0 0 10px rgba(152, 251, 152, 0.6)' }}>
                      📊 Detailed Analysis Data
                    </h5>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        navigator.clipboard.writeText(JSON.stringify(company, null, 2))
                        alert('Company data copied to clipboard!')
                      }}
                      className="btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '12px' }}
                    >
                      📋 Copy JSON
                    </button>
                  </div>

                  <pre style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    padding: '16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(152, 251, 152, 0.3)',
                    fontSize: '12px',
                    color: '#d0d0d0',
                    overflow: 'auto',
                    maxHeight: '400px',
                    fontFamily: 'Space Mono, Cousine, monospace',
                    lineHeight: '1.5'
                  }}>
                    {JSON.stringify(company, null, 2)}
                  </pre>
                </div>
              </details>
            ))}

            {result.citations && result.citations.length > 0 && (
              <div style={{ marginTop: '28px', padding: '20px', background: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                <h5 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '14px', color: 'white', textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)' }}>🔗 Sources & Citations</h5>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {result.citations.map((citation, idx) => (
                    <a
                      key={idx}
                      href={citation}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '13px', textDecoration: 'underline', textDecorationColor: 'rgba(255, 255, 255, 0.3)', wordBreak: 'break-all', transition: 'all 0.2s ease', textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)' }}
                    >
                      {citation}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <DeeperAnalysisDropdown
              analysisId={currentAnalysisId}
              supabase={supabase}
              initialDeeperAnalysis={deeperAnalysis}
            />
          </div>
        )}

        {history.length > 0 && (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <h3 style={{ fontSize: '24px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)', margin: 0 }}>
                &gt; Recent Operations
              </h3>
              <button
                onClick={() => navigate('/recent-searches')}
                className="btn-secondary"
                style={{ padding: '8px 16px', fontSize: '14px' }}
              >
                View All →
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Job Description</th>
                  <th>Top Match</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {history.slice(0, 5).map((item) => {
                  const companies = item.result_json?.companies || []
                  const topCompany = companies.length > 0 ? companies[0].name : 'N/A'
                  return (
                    <tr
                      key={item.id}
                      onClick={() => navigate(`/recent-searches?analysis_id=${item.id}`)}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#252525'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent'
                      }}
                    >
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(item.created_at).toLocaleDateString()}</td>
                      <td style={{ maxWidth: '300px' }}>{item.job_text.substring(0, 80)}...</td>
                      <td>{topCompany}</td>
                      <td>
                        <span className={`badge ${item.confidence_score >= 4 ? 'badge-high' : item.confidence_score >= 3 ? 'badge-medium' : 'badge-low'}`}>
                          {item.confidence_score}/5
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            </div>
            {history.length > 5 && (
              <div style={{ marginTop: '16px', textAlign: 'center', color: 'rgba(152, 251, 152, 0.75)', fontSize: '14px' }}>
                Showing 5 of {history.length} recent analyses
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={() => {
          setJobInput('')
          setShowClearConfirm(false)
        }}
        title="Clear Job Description?"
        message="Are you sure you want to clear the job description text? This action cannot be undone."
        confirmText="Clear Text"
        cancelText="Cancel"
        confirmStyle="danger"
      />

      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '100%', margin: 0 }}>
            <h3 style={{ fontSize: '24px', marginBottom: '20px', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
              &gt; Save This Search
            </h3>
            <div style={{ marginBottom: '20px' }}>
              <label className="label">Search Name</label>
              <input
                type="text"
                className="input"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g., Senior Accountant Milwaukee"
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowSaveModal(false)
                  setSaveName('')
                  setResultToSave(null)
                }}
                className="btn-secondary"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSearch}
                className="btn"
                style={{ flex: 1 }}
                disabled={!saveName.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
